import { DateTime } from "luxon";

/**
 * Shared date/period formatters for the PBJ Model audit + submission surfaces. Extracted from the
 * per-table copies (MissingPunches / Terminated / Employees / LongShifts) and the two tab headers
 * (Audit / SubmissionSheets) so every grid + header renders dates identically.
 */

/**
 * A date-cell formatter for the audit/submission grids. The row fields are typed `string` (ISO
 * yyyy-mm-dd) but the convertIsoDates axios interceptor may have already turned them into a Luxon
 * DateTime at runtime, so accept either shape. Renders as `DATE_MED` ("Jan 1, 2026"); null/invalid
 * → an em dash.
 */
export const formatAuditDate = (value: unknown): string => {
	const dt = toAuditDate(value);
	return dt ? dt.toLocaleString(DateTime.DATE_MED) : "—";
};

/** A grid date cell's value as a DateTime (either runtime shape — see `formatAuditDate`); null when blank or unreadable. */
export const toAuditDate = (value: unknown): DateTime | null => {
	if (value == null) return null;
	const dt = DateTime.isDateTime(value) ? value : DateTime.fromISO(String(value), { setZone: true });
	return dt.isValid ? dt : null;
};

/**
 * Compact "Jan 1 – Mar 31, 2026" period label for a tab-header subtitle, from a committed ISO range.
 * Drops the start year when both ends share it; returns null if either bound is missing/invalid so
 * the caller can omit the segment.
 */
export const formatPeriodRange = (start: string, end: string): string | null => {
	const s = start ? DateTime.fromISO(start) : null;
	const e = end ? DateTime.fromISO(end) : null;
	if (!s?.isValid || !e?.isValid) return null;
	const startFmt = s.year === e.year ? { month: "short", day: "numeric" } : DateTime.DATE_MED;
	return `${s.toLocaleString(startFmt as Intl.DateTimeFormatOptions)} – ${e.toLocaleString(DateTime.DATE_MED)}`;
};
